import { useTranslation } from "react-i18next";
import placeholder from "../states/Placeholder.module.css";
import { PlaceholderText } from "../states/PlaceholderText";
import { SectionGrid } from "../layout/SectionLayout";
import { useSectionLayout } from "../layout/sectionLayoutContext";
import card from "../tools/ToolCard.module.css";
import budgetStyles from "./OrganisationBudgets.module.css";
import styles from "./OrganisationLoading.module.css";

/** The breakdowns the page shows, in order, with the line each one opens with: sketched until the figures arrive. Each opens on its ranking. */
const sections = [
  { title: "organisation.spendByTool", subtitle: "organisation.sections.tool", withChart: false },
  { title: "organisation.byModel", subtitle: "organisation.sections.model", withChart: false },
  { title: "organisation.byDivision", subtitle: "organisation.sections.division", withChart: false },
  { title: "organisation.byDepartment", subtitle: "organisation.sections.department", withChart: false },
  { title: "organisation.byGroup", subtitle: "organisation.sections.group", withChart: false },
  { title: "organisation.byPerson", subtitle: "organisation.sections.person", withChart: false },
] as const;

/**
 * One section card, sketched: its header as it will read, drawn as soft bars, its finding, the chart's place where it has one,
 * then its rows.
 *
 * @param props.title - The section title's translation key.
 * @param props.subtitle - The section subtitle's translation key.
 * @param props.withChart - Whether the section shows a chart above its ranking.
 * @param props.rows - How many rows the layout lists.
 */
function PlaceholderSection({ title, subtitle, withChart, rows }: { title: string; subtitle: string; withChart: boolean; rows: number }): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <div className={`${card.card} ${styles.section}`} aria-hidden="true">
      <div className={card.header}>
        <span className={`${card.headerIcon} ${placeholder.pulse}`} />
        <div className={card.headerText}>
          <p className={card.title}>
            <PlaceholderText>{t(title)}</PlaceholderText>
          </p>
          <p className={card.subtitle}>
            <PlaceholderText>{t(subtitle)}</PlaceholderText>
          </p>
        </div>
      </div>
      <div className={`${placeholder.block} ${placeholder.line} ${styles.headline}`} />
      {withChart && (
        <>
          <div className={`${placeholder.block} ${styles.chart}`} />
          <div className={styles.legend}>
            <div className={`${placeholder.block} ${placeholder.line} ${styles.legendLine}`} />
          </div>
        </>
      )}
      <div className={styles.rows}>
        {Array.from({ length: rows }, (_, index) => (
          <div key={index} className={styles.row}>
            <div className={`${placeholder.block} ${placeholder.line} ${styles.rowHead}`} />
            <div className={`${placeholder.block} ${styles.rowBar}`} style={{ width: `${String(100 - index * 13)}%` }} />
          </div>
        ))}
      </div>
    </div>
  );
}

/** The Budgets section while it loads: its card, across the page, with a few units' rows. */
export function BudgetsLoading(): React.JSX.Element {
  return (
    <div className={budgetStyles.wide}>
      <PlaceholderSection title="budgets.organisation.title" subtitle="budgets.organisation.subtitle" withChart={false} rows={3} />
    </div>
  );
}

/**
 * The breakdowns while they load: each section card sketched in the layout the person chose, so the figures arrive in place.
 *
 * @param props.withPeople - Whether the page will show the people section (it names individuals, so not everyone sees it).
 */
export function BreakdownsLoading({ withPeople }: { withPeople: boolean }): React.JSX.Element {
  const { mode } = useSectionLayout();
  const shown = withPeople ? sections : sections.slice(0, -1);

  return (
    <SectionGrid>
      {shown.map((section) => (
        <PlaceholderSection key={section.title} title={section.title} subtitle={section.subtitle} withChart={section.withChart} rows={mode === "dense" ? 4 : 6} />
      ))}
    </SectionGrid>
  );
}

/** The "worth a look" list's place in the hero: a few rows' height. */
export function WorthALookLoading(): React.JSX.Element {
  return <div className={`${placeholder.block} ${styles.worthALook}`} aria-hidden="true" />;
}
